/**
 * The bootstrap wizard's three steps (CS r5b deliverable 163; flow G; 2.J): past sessions, a status
 * map from a select field, and the ledger's known and played marks. Each step is a preview, which
 * writes nothing, then an apply of the same request with the preview's `previewHash`, one batch;
 * a stale hash answers 409 `row_conflict`. NOW is `clock.advance` with `reason: 'bootstrap'`.
 */
import { CanonStatus } from '../domain/enums.js';
import { z } from '../zod.js';
import { Uuid } from './common.js';
import { DocTitle, TypeKey } from './documents.js';
import { FieldKey } from './types.js';
/** The most sessions one step creates, and the most targets the ledger step marks. */
export const BOOTSTRAP_SESSIONS_MAX = 500;
export const BOOTSTRAP_LEDGER_MAX = 1000;
/** The most options a status map names, and the most rows a preview lists. */
export const BOOTSTRAP_STATUS_MAP_MAX = 200;
export const BOOTSTRAP_PREVIEW_ROWS_MAX = 1000;
/** SHA-256 hex of the preview (2.J's `previewHash`): an apply names the preview it applies. */
export const PreviewHash = z.string().regex(/^[0-9a-f]{64}$/);
const RealDate = z.iso.date();
/** Step 1: the past sessions, by number, with their real date and title when known. */
export const BootstrapSessionsV1 = z.strictObject({
    sessions: z
        .array(z.strictObject({
        number: z.number().int().min(1),
        realDate: RealDate.optional(),
        title: DocTitle.optional(),
    }))
        .min(1)
        .max(BOOTSTRAP_SESSIONS_MAX),
});
/** A status map's canon statuses: never `reference` (the SRD's own). */
export const BOOTSTRAP_CANON_STATUSES = ['draft', 'canon', 'retired'];
/** A status map's play states: a before-tracking document-level play record. */
export const BOOTSTRAP_PLAY_STATES = ['played', 'skipped'];
/**
 * Step 2: a type's select field read as canon status and play state: each option maps to a canon
 * status (not `reference`) or none, and a play state (`played`, `skipped`) or none.
 */
export const BootstrapStatusV1 = z.strictObject({
    typeKey: TypeKey,
    fieldKey: FieldKey,
    map: z
        .array(z.strictObject({
        option: z.string().min(1).max(200),
        canonStatus: z.enum(BOOTSTRAP_CANON_STATUSES).nullable(),
        playState: z.enum(BOOTSTRAP_PLAY_STATES).nullable(),
    }))
        .min(1)
        .max(BOOTSTRAP_STATUS_MAP_MAX),
});
/** A ledger target: a secret or a document, never both. */
export const BootstrapLedgerTargetV1 = z.union([
    z.strictObject({ secretId: Uuid }),
    z.strictObject({ documentId: Uuid }),
]);
/**
 * Step 3: targets marked `known` (an assertion) or `played` (a document-level play record), as of
 * a played session's number or before tracking began.
 */
export const BootstrapLedgerV1 = z.strictObject({
    targets: z.array(BootstrapLedgerTargetV1).min(1).max(BOOTSTRAP_LEDGER_MAX),
    mark: z.enum(['known', 'played']),
    asOf: z.union([z.number().int().min(1), z.literal('before_tracking')]),
});
const applyOf = (request) => request.extend({ previewHash: PreviewHash });
/** Each apply: its step's request and the hash of the preview it applies. */
export const BootstrapSessionsApplyV1 = applyOf(BootstrapSessionsV1);
export const BootstrapStatusApplyV1 = applyOf(BootstrapStatusV1);
export const BootstrapLedgerApplyV1 = applyOf(BootstrapLedgerV1);
/**
 * What a preview row decides: a session `create`d, `untrash`ed (2.J's "restore" of a trashed
 * bootstrap session: `restore` is a declined field name, the backups the GM declined) or
 * `skip`ped; a document's status `update`d; a ledger or play `record` written; or `unchanged`.
 */
export const BOOTSTRAP_DECISIONS = [
    'create',
    'untrash',
    'skip',
    'update',
    'record',
    'unchanged',
];
export const BootstrapDecision = z.enum(BOOTSTRAP_DECISIONS);
/** The count of each decision, every one present (0 when none). */
const DecisionCounts = z.strictObject(Object.fromEntries(BOOTSTRAP_DECISIONS.map((d) => [d, z.number().int().min(0)])));
/**
 * One row of a preview: its decision and what it names (the request's index, a session number, a
 * document or secret) with the fields shown beside it. `dateKept` marks an untrashed session whose
 * stored real date stays (2.J's open point 4).
 */
export const BootstrapPreviewRowV1 = z.strictObject({
    decision: BootstrapDecision,
    index: z.number().int().min(0).optional(),
    number: z.number().int().min(1).optional(),
    documentId: Uuid.optional(),
    secretId: Uuid.optional(),
    title: z.string().max(500).optional(),
    realDate: RealDate.optional(),
    canonStatus: CanonStatus.optional(),
    playState: z.enum(BOOTSTRAP_PLAY_STATES).optional(),
    dateKept: z.boolean().optional(),
});
/** A step's preview: its hash, the decisions' counts and the rows. Nothing is written. */
export const BootstrapPreviewV1 = z.strictObject({
    previewHash: PreviewHash,
    counts: DecisionCounts,
    rows: z.array(BootstrapPreviewRowV1).max(BOOTSTRAP_PREVIEW_ROWS_MAX),
});
/** An apply's answer: its one batch (also in `CHANGE_BATCH_HEADER`) and the decisions' counts. */
export const BootstrapAppliedV1 = z.strictObject({
    batchId: Uuid,
    counts: DecisionCounts,
});
//# sourceMappingURL=bootstrap.js.map